import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiCalendar, FiSearch, FiClock, FiBookOpen } from 'react-icons/fi';
import LoadingSpinner from '../components/LoadingSpinner';
import { blogService, courseService } from '../services/api';
import { useSettings } from '../contexts/SettingsContext';
import ScrollAnimation from '../components/ScrollAnimation';

const Page = styled.div`
	width: 100%;
	overflow-x: hidden;
	background: ${p => p.theme.colors.gray[50]};
`;

const Container = styled.div`
	max-width: ${p => p.theme.breakpoints.wide};
	margin: 0 auto;
	padding: 0 ${p => p.theme.spacing.lg};

	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: 0 ${p => p.theme.spacing.md};
	}
`;

const HeroSection = styled.div`
	background: ${p => p.theme.colors.gradient.primary};
	padding: ${p => p.theme.spacing['4xl']} 0 ${p => p.theme.spacing['3xl']};
	text-align: center;
	color: ${p => p.theme.colors.light};
	position: relative;
	overflow: hidden;
	
	&::before {
		content: '';
		position: absolute;
		top: -50%;
		right: -10%;
		width: 500px;
		height: 500px;
		background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
		border-radius: 50%;
	}
	
	&::after {
		content: '';
		position: absolute;
		bottom: -30%;
		left: -10%;
		width: 400px;
		height: 400px;
		background: radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%);
		border-radius: 50%;
	}
	
	@media (max-width: ${p => p.theme.breakpoints.tablet}) {
		padding: ${p => p.theme.spacing['3xl']} 0 ${p => p.theme.spacing['2xl']};
	}
`;

const HeroContent = styled.div`
	position: relative;
	z-index: 1;
	max-width: 800px;
	margin: 0 auto;
	padding: 0 ${p => p.theme.spacing.lg};
	
	h1 {
		font-size: ${p => p.theme.fontSizes['4xl']};
		font-weight: ${p => p.theme.fontWeights.bold};
		margin-bottom: ${p => p.theme.spacing.md};
		
		@media (max-width: ${p => p.theme.breakpoints.tablet}) {
			font-size: ${p => p.theme.fontSizes['3xl']};
		}
	}
	
	p {
		font-size: ${p => p.theme.fontSizes.lg};
		opacity: 0.95;
		margin-bottom: ${p => p.theme.spacing['2xl']};
		
		@media (max-width: ${p => p.theme.breakpoints.tablet}) {
			font-size: ${p => p.theme.fontSizes.base};
		}
	}
	
	.stats {
		display: flex;
		justify-content: center;
		gap: ${p => p.theme.spacing['2xl']};
		margin-top: ${p => p.theme.spacing.xl};
		flex-wrap: wrap;
		
		.stat {
			display: flex;
			align-items: center;
			gap: ${p => p.theme.spacing.sm};
			font-size: ${p => p.theme.fontSizes.base};
			
			@media (max-width: ${p => p.theme.breakpoints.mobile}) {
				font-size: ${p => p.theme.fontSizes.sm};
			}
			
			svg {
				font-size: 24px;
				
				@media (max-width: ${p => p.theme.breakpoints.mobile}) {
					font-size: 20px;
				}
			}
		}
	}
`;

const SearchBox = styled.div`
	position: relative;
	width: 100%;
	margin-bottom: ${p => p.theme.spacing.lg};

	&.mobile-search {
		display: none;
		margin-top: ${p => p.theme.spacing.xl};
		
		@media (max-width: ${p => p.theme.breakpoints.desktop}) {
			display: block;
		}
	}

	&.desktop-search {
		display: block;
		
		@media (max-width: ${p => p.theme.breakpoints.desktop}) {
			display: none;
		}
	}

	input{
		width: 100%;
		padding: 14px 50px;
		border-radius: ${p => p.theme.borderRadius.full};
		border: 2px solid ${p => p.theme.colors.gray[300]};
		background: ${p => p.theme.colors.light};
		font-size: ${p => p.theme.fontSizes.base};
		transition: ${p => p.theme.transitions.fast};
		box-shadow: ${p => p.theme.shadows.lg};

		&:focus {
			outline: none;
			border-color: ${p => p.theme.colors.primary};
			box-shadow: 0 0 0 4px ${p => p.theme.colors.primary}22, ${p => p.theme.shadows.xl};
		}

		&:hover {
			border-color: ${p => p.theme.colors.gray[400]};
		}
	}
	
	svg{
		position: absolute;
		left: 16px; 
		top: 50%; 
		transform: translateY(-50%);
		color: ${p => p.theme.colors.primary};
		font-size: 20px;
		transition: ${p => p.theme.transitions.fast};
	}

	input:focus ~ svg {
		color: ${p => p.theme.colors.primary};
		transform: translateY(-50%) scale(1.15);
	}
`;

const Grid = styled.div`
	display: grid; 
	grid-template-columns: 1.8fr 1fr; 
	gap: ${p => p.theme.spacing['2xl']};
	padding-bottom: ${p => p.theme.spacing['3xl']};
	padding-top: ${p => p.theme.spacing['2xl']};
	
	@media (max-width: ${p => p.theme.breakpoints.desktop}){ 
		grid-template-columns: 1fr;
		padding-top: 0;
		gap: ${p => p.theme.spacing.lg};
	}

	main {
		@media (max-width: ${p => p.theme.breakpoints.desktop}){ 
			padding-top: 0 !important;
		}
	}
`;

// Post list item (horizontal)
const PostItem = styled.article`
	background: ${p => p.theme.colors.light};
	border: 2px solid ${p => p.theme.colors.primary}22;
	border-radius: ${p => p.theme.borderRadius.xl};
	overflow: hidden; 
	display: flex; 
	box-shadow: 0 4px 12px ${p => p.theme.colors.primary}15;
	transition: ${p => p.theme.transitions.default};
	height: 160px;

	&:hover {
		transform: translateY(-8px); 
		box-shadow: 0 8px 24px ${p => p.theme.colors.primary}25;
		border-color: ${p => p.theme.colors.primary}44;
	}

	@media (max-width: ${p => p.theme.breakpoints.tablet}){ 
		flex-direction: column; 
		height: auto;
	}
`;

const Thumb = styled.div`
	flex: 0 0 240px; 
	height: 160px;
	background: ${p => p.theme.colors.gradient.primary}; 
	overflow: hidden;

	@media (max-width: ${p => p.theme.breakpoints.tablet}){ 
		flex: 0 0 180px;
		height: 180px;
	}
`;

const ThumbImg = styled.img`
	display: block;
	width: 100%; 
	height: 160px; 
	object-fit: cover;
	object-position: center;
	transition: ${p => p.theme.transitions.default};
	margin: 0;
	padding: 0;
	border: none;
	
	${PostItem}:hover & {
		transform: scale(1.05);
	}
	
	@media (max-width: ${p => p.theme.breakpoints.tablet}){ 
		height: 180px;
	}
`;

const PostBody = styled.div`
	flex: 1; 
	padding: ${p => p.theme.spacing.lg}; 
	display: flex; 
	flex-direction: column; 
	gap: ${p => p.theme.spacing.sm};
	justify-content: flex-start;
	padding-top: ${p => p.theme.spacing.md};
`;

const Meta = styled.div`
	display: flex; 
	flex-wrap: wrap; 
	gap: ${p => p.theme.spacing.lg}; 
	color: ${p => p.theme.colors.gray[600]}; 
	font-size: ${p => p.theme.fontSizes.sm};
	
	.meta{ 
		display: flex; 
		align-items: center; 
		gap: 6px;
		
		svg {
			color: ${p => p.theme.colors.primary};
		}
	}
`;

const Title = styled.h3`
	margin: 0; 
	font-size: ${p => p.theme.fontSizes.xl};
	line-height: 1.4;
	font-weight: ${p => p.theme.fontWeights.bold};
	color: ${p => p.theme.colors.dark};
	transition: ${p => p.theme.transitions.fast};
	
	a{ 
		color: inherit; 
		text-decoration: none;
		
		&:hover {
			color: ${p => p.theme.colors.primary};
		}
	}
`;

const Excerpt = styled.p`
	margin: 0; 
	color: ${p => p.theme.colors.gray[600]}; 
	line-height: 1.5;
	font-size: ${p => p.theme.fontSizes.sm};
	display: -webkit-box; 
	-webkit-line-clamp: 2; 
	-webkit-box-orient: vertical; 
	overflow: hidden;
`;

const List = styled.div`
	display: grid; gap: ${p => p.theme.spacing['2xl']};
`;

// Sidebar
const Card = styled.section`
	background: ${p=>p.theme.colors.light}; 
	border: 1px solid ${p=>p.theme.colors.gray[200]}; 
	border-radius: ${p=>p.theme.borderRadius.xl};
	box-shadow: ${p=>p.theme.shadows.md}; 
	overflow: hidden;
	transition: ${p=>p.theme.transitions.default};
	position: relative;

	&::after {
		content: '';
		position: absolute;
		bottom: 0;
		left: 0;
		right: 0;
		height: 3px;
		background: ${p=>p.theme.colors.gradient.primary};
		opacity: 0;
		transition: opacity 0.3s ease;
	}

	&:hover {
		box-shadow: ${p=>p.theme.shadows.lg};
	}

	&:hover::after {
		opacity: 1;
	}
`;
const CardHeader = styled.div`
	padding: ${p=>p.theme.spacing.lg} ${p=>p.theme.spacing.xl}; 
	border-bottom: 1px solid ${p=>p.theme.colors.gray[200]}; 
	font-weight: 800;
	background: linear-gradient(to right, ${p=>p.theme.colors.gray[50]}, ${p=>p.theme.colors.light});
	color: ${p=>p.theme.colors.dark};
	font-size: ${p=>p.theme.fontSizes.lg};
`;
const CardBody = styled.div`
	padding:${p=>p.theme.spacing.xl}; display:grid; gap:${p=>p.theme.spacing.lg};
`;

const TopPost = styled(Link)`
	display: grid; 
	grid-template-columns: 72px 1fr; 
	gap: ${p=>p.theme.spacing.md}; 
	color: inherit; 
	text-decoration: none;
	padding: ${p=>p.theme.spacing.sm};
	border-radius: ${p=>p.theme.borderRadius.md};
	transition: ${p=>p.theme.transitions.fast};

	&:hover { 
		background: ${p=>p.theme.colors.gray[50]};
		color: ${p=>p.theme.colors.primary}; 
		transform: translateX(4px);
	}
`;
const TopThumb = styled.img`
	width: 72px; 
	height: 72px; 
	object-fit: cover; 
	border-radius: ${p=>p.theme.borderRadius.md}; 
	background: ${p=>p.theme.colors.gray[100]};
	box-shadow: ${p=>p.theme.shadows.sm};
	transition: ${p=>p.theme.transitions.fast};

	${TopPost}:hover & {
		box-shadow: ${p=>p.theme.shadows.md};
		transform: scale(1.05);
	}
`;
const Small = styled.small`
	color:${p=>p.theme.colors.gray[600]};
`;

// Helpers
const formatDate = (d) => {
	if (!d) return '';
	const dt = new Date(d);
	return dt.toLocaleDateString('en-US', { year:'numeric', month:'short', day:'numeric' });
};

export default function Blog() {
	const { settings } = useSettings();
	const [loading, setLoading] = useState(true);
	const [posts, setPosts] = useState([]);
	const [query, setQuery] = useState('');
	const [featured, setFeatured] = useState([]);

	useEffect(() => {
		const load = async () => {
			setLoading(true);
			try {
				const data = await blogService.getBlogs();
				setPosts(Array.isArray(data) ? data : []);
					// Fetch featured courses in parallel
					try {
						const fc = await courseService.getFeaturedCourses();
						setFeatured(Array.isArray(fc) ? fc.slice(0,3) : []);
					} catch (err) {
						setFeatured([]);
					}
			} catch (e) {
				// eslint-disable-next-line no-console
				setPosts([]);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, []);

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		if (!q) return posts;
		return posts.filter(p => [p.title, p.excerpt, p.summary, p.subtitle]
			.filter(Boolean)
			.some(t => String(t).toLowerCase().includes(q))
		);
	}, [posts, query]);

	const topPosts = useMemo(() => {
		const arr = [...posts];
		// Prefer backend ranking fields if present
		arr.sort((a,b) => (b.views||b.reads||Date.parse(b.date||0)) - (a.views||a.reads||Date.parse(a.date||0)));
		return arr.slice(0,5);
	}, [posts]);

	// Redirect if blog is disabled (after all hooks)
	if (settings?.features?.blogEnabled === false) {
		return <Navigate to="/" replace />;
	}

	if (loading) return <LoadingSpinner fullScreen text="Loading blog posts..." />;

	return (
		<Page>
			<HeroSection>
				<HeroContent>
					<h1>📚 Discover our latest posts</h1>
					<p>
						Explore insights, tutorials, and industry trends to accelerate your tech journey
					</p>
					<div className="stats">
						<div className="stat">
							<FiBookOpen />
							<span>{posts.length} Articles</span>
						</div>
						<div className="stat">
							<FiClock />
							<span>Updated Weekly</span>
						</div>
					</div>
				</HeroContent>
			</HeroSection>

			<Container>
				<Grid>
					<main>
						<SearchBox className="mobile-search">
							<FiSearch />
							<input
								value={query}
								onChange={e=>setQuery(e.target.value)}
								placeholder="Search posts..."
								aria-label="Search posts"
							/>
						</SearchBox>

						<List>
											{filtered.map((p, idx) => {
												const img = p.image || p.thumbnail || p.coverImage || p.banner;
												const excerpt = p.excerpt || p.summary || p.subtitle || p.description || '';
												return (
													<ScrollAnimation key={idx} animation="fadeInUp" delay={idx * 50}>
														<PostItem>
															<Thumb>
																{img && <ThumbImg src={img} alt={p.title} onError={(e)=>{ e.currentTarget.style.display='none'; }} />}
															</Thumb>
															<PostBody>
																<Meta>
																	{p.date && <span className="meta"><FiCalendar /> {formatDate(p.date)}</span>}
																</Meta>
																<Title><Link to={`/blog/${p.slug}`}>{p.title}</Link></Title>
																{excerpt && <Excerpt>{excerpt}</Excerpt>}
															</PostBody>
														</PostItem>
													</ScrollAnimation>
												);
											})}
							{filtered.length === 0 && (
								<Small>No posts found.</Small>
							)}
						</List>
					</main>

					<aside>
						<SearchBox className="desktop-search">
							<FiSearch />
							<input
								value={query}
								onChange={e=>setQuery(e.target.value)}
								placeholder="Search posts..."
								aria-label="Search posts"
							/>
						</SearchBox>

						{topPosts.length > 0 && (
							<Card>
								<CardHeader>Top Posts</CardHeader>
								<CardBody>
													{topPosts.map((p, i) => {
														const topImg = p.thumbnail || p.image || p.coverImage || '';
														return (
															<TopPost key={i} to={`/blog/${p.slug}`}>
																{topImg ? (
																	<TopThumb src={topImg} alt={p.title} onError={(e)=>{e.currentTarget.style.display='none';}} />
																) : (
																	<div />
																)}
																<div>
																	<div style={{ fontWeight: 700, marginBottom: 4 }}>{p.title}</div>
																	{!topImg && p.date && <Small>{formatDate(p.date)}</Small>}
																</div>
															</TopPost>
														);
													})}
								</CardBody>
							</Card>
						)}

										{featured.length > 0 && (
											<Card style={{ marginTop: '1.25rem' }}>
												<CardHeader>Featured Courses</CardHeader>
												<CardBody>
													{featured.map((c, i) => (
														<TopPost key={i} to={`/courses/${c.slug}`}>
															<TopThumb src={c.thumbnail || c.image || ''} alt={c.title} onError={(e)=>{e.currentTarget.style.display='none';}} />
															<div>
																<div style={{ fontWeight: 700, marginBottom: 4 }}>{c.title}</div>
																<Small>{c.level ? c.level.charAt(0).toUpperCase()+c.level.slice(1) : ''}{c.duration ? (c.level ? ' • ' : '') + c.duration : ''}</Small>
															</div>
														</TopPost>
													))}
												</CardBody>
											</Card>
										)}
					</aside>
				</Grid>
			</Container>
		</Page>
	);
}

